import { setAriaFlag, setAriaState, setLinkAriaCurrent } from '@/core/utils/a11y'
import type { TaxiNavigateInEvent } from '@/types/taxi'
import type { ThemeEditorSectionUnloadEvent } from '@/types/shopify'

import BaseSection from '@/sections/base'
import HeaderCartControl from '@/components/header/headerCartControl'

const selectors = {
  toggleButton: '[data-grid-toggle]',
  toggleIcon: '[data-grid-toggle-icon]',
  menuLinks: '.header-nav__list[aria-label="Main"] ul',
  mobileMenuToggle: '[data-mobile-menu-toggle]',
  mobileMenuOverlay: '[data-mobile-menu-overlay]'
}

const GRID_STORAGE_KEY = 'featured-products.grid-columns'
const GRID_TOGGLE_LABEL = 'Cycle product grid view'
const BACK_TO_SHOP_LABEL = 'Back to Shop'
const SHOP_COLLECTION_PATH = '/collections/all-products'
const MOBILE_MEDIA_QUERY = '(max-width: 63.98rem)'

const GRID_MODE_LABELS: Record<GridMode, string> = {
  '3': 'View: 3',
  '6': 'View: 6',
  '2': 'View: 2',
  '1': 'View: 1',
  list: 'View: List'
}

type GridMode = '3' | '6' | '2' | '1' | 'list'

export default class HeaderSection extends BaseSection {
  static TYPE = 'header'

  headerCartControl: HeaderCartControl
  toggleButton: HTMLButtonElement | undefined
  toggleIcon: HTMLElement | undefined
  menuLinks: HTMLElement | undefined
  mobileMenuToggle: HTMLButtonElement | undefined
  mobileMenuOverlay: HTMLElement | undefined
  currentMode: GridMode
  mobileQuery: MediaQueryList

  onControlClickBound: (e: Event) => void
  onViewportChangeBound: () => void
  onMobileMenuToggleClickBound: (e: MouseEvent) => void
  onDocumentClickBound: (e: MouseEvent) => void

  constructor(container: HTMLElement) {
    super(container)

    this.headerCartControl = new HeaderCartControl(this.qs(HeaderCartControl.SELECTOR))
    this.toggleButton = this.qs(selectors.toggleButton) as HTMLButtonElement | undefined
    this.toggleIcon = this.qs(selectors.toggleIcon)
    this.menuLinks = this.qs(selectors.menuLinks)
    this.mobileMenuToggle = this.qs(selectors.mobileMenuToggle) as HTMLButtonElement | undefined
    this.mobileMenuOverlay = this.qs(selectors.mobileMenuOverlay)
    this.currentMode = '6'
    this.mobileQuery = window.matchMedia(MOBILE_MEDIA_QUERY)

    this.onControlClickBound = this.onControlClick.bind(this)
    this.onViewportChangeBound = this.onViewportChange.bind(this)
    this.onMobileMenuToggleClickBound = this.onMobileMenuToggleClick.bind(this)
    this.onDocumentClickBound = this.onDocumentClick.bind(this)

    this.initializeGridControls()
  }

  onUnload(e: ThemeEditorSectionUnloadEvent) {
    this.toggleButton?.removeEventListener('click', this.onControlClickBound)
    this.mobileQuery.removeEventListener('change', this.onViewportChangeBound)
    this.mobileMenuToggle?.removeEventListener('click', this.onMobileMenuToggleClickBound)
    document.removeEventListener('click', this.onDocumentClickBound)

    super.onUnload(e)
  }

  isMobileView() {
    return this.mobileQuery.matches
  }

  initializeGridControls() {
    const storedMode = window.localStorage.getItem(GRID_STORAGE_KEY)

    this.currentMode = this.normalizeGridMode(storedMode)

    this.toggleButton?.addEventListener('click', this.onControlClickBound)
    this.mobileQuery.addEventListener('change', this.onViewportChangeBound)
    this.mobileMenuToggle?.addEventListener('click', this.onMobileMenuToggleClickBound)
    document.addEventListener('click', this.onDocumentClickBound)
    this.updateControlState(window.location.pathname)
    this.updateMenuLinksVisibility(window.location.pathname)
    this.dispatchGridMode(this.currentMode)
    this.setMobileMenuOpen(false)
  }

  normalizeGridMode(rawMode: string | null, forceMobileView?: boolean): GridMode {
    const isMobileView = forceMobileView ?? this.isMobileView()

    if (rawMode === 'list') return 'list'

    if (isMobileView) {
      if (rawMode === '2' || rawMode === '1') return rawMode
      if (rawMode === '3') return '2'
      if (rawMode === '6') return '1'
    }
    else {
      if (rawMode === '3' || rawMode === '6') return rawMode
      if (rawMode === '2') return '3'
      if (rawMode === '1') return '6'
    }

    if (rawMode === 'L' || rawMode === 'l') return 'list'

    const parsedMode = Number.parseInt(rawMode || (isMobileView ? '2' : '6'), 10)

    if (isMobileView) {
      if (parsedMode === 2 || parsedMode === 3) return '2'
      if (parsedMode === 1 || parsedMode === 6) return '1'

      return '2'
    }

    if (parsedMode === 3 || parsedMode === 2) return '3'
    if (parsedMode === 6 || parsedMode === 1) return '6'

    return '6'
  }

  nextGridMode(mode: GridMode): GridMode {
    if (this.isMobileView()) {
      if (mode === '2') return '1'
      if (mode === '1') return 'list'

      return '2'
    }

    if (mode === '3') return '6'
    if (mode === '6') return 'list'

    return '3'
  }

  isProductPath(pathname: string) {
    return pathname.startsWith('/products/') || pathname.includes('/products/')
  }

  isCollectionPath(pathname: string) {
    return (pathname === '/collections' || pathname.startsWith('/collections/')) && !this.isProductPath(pathname)
  }

  updateMenuLinksVisibility(pathname: string) {
    if (!this.menuLinks) return

    const show = this.isCollectionPath(pathname)

    this.menuLinks.style.display = show ? '' : 'none'
  }

  updateControlState(pathname: string) {
    const isCollectionPage = this.isCollectionPath(pathname)

    if (this.toggleIcon) {
      if (isCollectionPage) {
        this.toggleIcon.textContent = GRID_MODE_LABELS[this.currentMode]
      }
      else {
        this.toggleIcon.textContent = BACK_TO_SHOP_LABEL
      }
    }

    this.toggleButton?.setAttribute('aria-pressed', 'false')
    this.toggleButton?.setAttribute('aria-label', isCollectionPage ? GRID_TOGGLE_LABEL : BACK_TO_SHOP_LABEL)
  }

  navigateToShop() {
    if (window.app?.taxi) {
      window.app.taxi.navigateTo(SHOP_COLLECTION_PATH)
    }
    else {
      window.location.href = SHOP_COLLECTION_PATH
    }
  }

  dispatchGridMode(mode: GridMode) {
    const normalizedMode = this.normalizeGridMode(mode)

    this.currentMode = normalizedMode

    window.dispatchEvent(new CustomEvent('featuredProducts.setColumns', {
      detail: {
        mode: normalizedMode,
      }
    }))
  }

  onViewportChange() {
    const storedMode = window.localStorage.getItem(GRID_STORAGE_KEY)

    this.currentMode = this.normalizeGridMode(storedMode || this.currentMode)
    this.setMobileMenuOpen(false)
    this.updateControlState(window.location.pathname)
    this.dispatchGridMode(this.currentMode)
  }

  setMobileMenuOpen(open: boolean) {
    if (!this.mobileMenuOverlay || !this.mobileMenuToggle) return

    setAriaFlag(this.mobileMenuOverlay, 'aria-hidden', !open)
    setAriaState(this.mobileMenuToggle, 'aria-expanded', open)
  }

  isMobileMenuOpen() {
    return Boolean(this.mobileMenuOverlay && this.mobileMenuOverlay.getAttribute('aria-hidden') !== 'true')
  }

  onMobileMenuToggleClick(e: MouseEvent) {
    e.preventDefault()

    this.setMobileMenuOpen(!this.isMobileMenuOpen())
  }

  onDocumentClick(e: MouseEvent) {
    if (!this.isMobileMenuOpen() || !this.mobileMenuOverlay || !this.mobileMenuToggle) return

    const target = e.target as HTMLElement

    if (this.mobileMenuOverlay.contains(target) || this.mobileMenuToggle.contains(target)) return

    this.setMobileMenuOpen(false)
  }

  onControlClick(e: Event) {
    const button = e.currentTarget as HTMLButtonElement | null

    if (!button) return

    if (!this.isCollectionPath(window.location.pathname)) {
      this.navigateToShop()
      return
    }

    this.currentMode = this.nextGridMode(this.currentMode)

    window.localStorage.setItem(GRID_STORAGE_KEY, this.currentMode)
    this.updateControlState(window.location.pathname)
    this.dispatchGridMode(this.currentMode)
  }

  onNavigateIn(e: TaxiNavigateInEvent) {
    const currentPath = new URL(e.detail.to.finalUrl).pathname
    const links = this.container.querySelectorAll<HTMLAnchorElement>('nav a')

    links.forEach(link => setLinkAriaCurrent(link, currentPath))
    this.setMobileMenuOpen(false)
    this.updateControlState(currentPath)
    this.updateMenuLinksVisibility(currentPath)
  }  
}
